(function () {
  const STORAGE_KEY = 'paris-wishlist-items';
  const MAX_ITEMS = 40;
  const BUTTON_SELECTOR = '[data-wishlist-button]';
  const DRAWER_SELECTOR = '[data-wishlist-drawer-content]';
  const COUNT_SELECTOR = '[data-header-wishlist-count]';
  const SYNC_SELECTOR = `${BUTTON_SELECTOR}, ${DRAWER_SELECTOR}, ${COUNT_SELECTOR}`;

  function getItems() {
    try {
      const storedItems = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(storedItems)) {
        return [];
      }

      const seenIds = new Set();
      return storedItems
        .filter((item) => item && item.id)
        .filter((item) => {
          const itemId = String(item.id);
          if (seenIds.has(itemId)) {
            return false;
          }
          seenIds.add(itemId);
          return true;
        });
    } catch (error) {
      return [];
    }
  }

  function setItems(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch (error) {
      return;
    }
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeItem(button) {
    return {
      id: String(button.dataset.productId || ''),
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '#',
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || '',
    };
  }

  function findItemIndex(items, productId) {
    return items.findIndex((item) => String(item.id) === String(productId));
  }

  function updateButtons() {
    const items = getItems();

    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      const isActive = findItemIndex(items, button.dataset.productId) > -1;
      const labelElement = button.querySelector('[data-wishlist-button-label]');

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));

      if (labelElement) {
        if (!button.dataset.wishlistLabelDefault) {
          button.dataset.wishlistLabelDefault = labelElement.textContent.trim();
        }

        labelElement.textContent = isActive ? 'Favourited' : button.dataset.wishlistLabelDefault;
      }
    });
  }

  function updateHeaderCount() {
    const count = getItems().length;
    const countLabel = count > 99 ? '99+' : String(count);

    document.querySelectorAll(COUNT_SELECTOR).forEach((countElement) => {
      countElement.textContent = countLabel;
      countElement.classList.toggle('hidden', count === 0);
    });
  }

  function renderDrawer() {
    const items = getItems();
    const markup = items.length
      ? `<div class="wishlist-drawer-list">${items
          .map(
            (item) => `
              <div class="wishlist-drawer-item">
                <a class="wishlist-drawer-item-image" href="${escapeHtml(item.url)}" aria-label="${escapeHtml(item.title)}">
                  ${
                    item.image
                      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">`
                      : `<span class="wishlist-drawer-item-placeholder">No image</span>`
                  }
                </a>
                <div class="wishlist-drawer-item-details">
                  <a class="wishlist-drawer-item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
                  ${item.price ? `<p class="wishlist-drawer-item-price">${escapeHtml(item.price)}</p>` : ''}
                  <div class="wishlist-drawer-item-actions">
                    <a href="${escapeHtml(item.url)}" class="text-underline">View product</a>
                    <button type="button" class="wishlist-remove-button" data-wishlist-remove data-product-id="${escapeHtml(item.id)}">Remove</button>
                  </div>
                </div>
              </div>
            `
          )
          .join('')}</div>`
      : `
        <div class="wishlist-empty-state">
          <h6>Your favourites list is empty</h6>
          <p>Tap the heart icon on any product to save it here.</p>
        </div>
      `;

    document.querySelectorAll(DRAWER_SELECTOR).forEach((drawerContent) => {
      drawerContent.innerHTML = markup;
    });
  }

  function syncWishlistUI() {
    updateButtons();
    updateHeaderCount();
    renderDrawer();
  }

  function toggleWishlistItem(button) {
    const item = normalizeItem(button);
    if (!item.id) {
      return;
    }

    const items = getItems();
    const existingIndex = findItemIndex(items, item.id);

    if (existingIndex > -1) {
      items.splice(existingIndex, 1);
    } else {
      items.unshift(item);
    }

    setItems(items);
    syncWishlistUI();
  }

  function removeWishlistItem(productId) {
    const items = getItems();
    const itemIndex = findItemIndex(items, productId);

    if (itemIndex === -1) {
      return;
    }

    items.splice(itemIndex, 1);
    setItems(items);
    syncWishlistUI();
  }

  function handleDocumentClick(event) {
    const wishlistButton = event.target.closest(BUTTON_SELECTOR);
    if (wishlistButton) {
      event.preventDefault();
      toggleWishlistItem(wishlistButton);
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      removeWishlistItem(removeButton.dataset.productId);
    }
  }

  function observeWishlistDom() {
    if (!document.body || document.body.dataset.wishlistObserved === 'true') {
      return;
    }

    document.body.dataset.wishlistObserved = 'true';

    const observer = new MutationObserver((mutations) => {
      const shouldSync = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof HTMLElement)) {
            return false;
          }

          return node.matches(SYNC_SELECTOR) || node.querySelector(SYNC_SELECTOR);
        })
      );

      if (shouldSync) {
        syncWishlistUI();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function initWishlist() {
    if (document.documentElement.dataset.wishlistInitialized !== 'true') {
      document.documentElement.dataset.wishlistInitialized = 'true';
      document.addEventListener('click', handleDocumentClick);
      window.addEventListener('storage', function (event) {
        if (event.key === STORAGE_KEY) {
          syncWishlistUI();
        }
      });
      observeWishlistDom();
    }

    syncWishlistUI();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist);
  } else {
    initWishlist();
  }

  document.addEventListener('shopify:section:load', initWishlist);
})();
